/**
 * Reset Password Screen Component
 *
 * DEPRECATED: Legacy Supabase Auth implementation
 * Phase 6 (2026-02-11): Azure Migration Complete
 * TODO: Replace with Azure auth password reset API
 *
 * Shown when user clicks password reset link from email
 * Allows setting new password after recovery token validation
 */

import React, { useState } from 'react';
import { Ship, Lock, Eye, EyeOff, CheckCircle, XCircle, Loader2 } from 'lucide-react';
// DEPRECATED: Supabase auth removed in Phase 6 Azure migration
// import { updatePassword } from '../../../lib/supabaseAuth';
import { useCustomDialogs } from '../../../hooks/useCustomDialogs';

export const ResetPasswordScreen = ({ onResetComplete }) => {
  const { showAlert } = useCustomDialogs();
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Form state
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  // Show/hide password toggles
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Error state
  const [errors, setErrors] = useState({
    newPassword: '',
    confirmPassword: ''
  });

  // Password strength calculation (reused from PasswordChangeCard.js lines 36-79)
  const calculatePasswordStrength = (password) => {
    if (!password) return { score: 0, label: '', color: '', checks: {} };

    let score = 0;
    const checks = {
      length: password.length >= 8,
      uppercase: /[A-Z]/.test(password),
      lowercase: /[a-z]/.test(password),
      number: /[0-9]/.test(password),
      special: /[!@#$%^&*(),.?":{}|<>]/.test(password)
    };

    if (checks.length) score += 20;
    if (checks.uppercase) score += 20;
    if (checks.lowercase) score += 20;
    if (checks.number) score += 20;
    if (checks.special) score += 20;

    // Length bonus
    if (password.length >= 12) score += 10;
    if (password.length >= 16) score += 10;

    // Cap at 100
    score = Math.min(score, 100);

    let label = '';
    let color = '';

    if (score < 40) {
      label = 'Weak';
      color = 'text-red-600 bg-red-100';
    } else if (score < 70) {
      label = 'Fair';
      color = 'text-yellow-600 bg-yellow-100';
    } else if (score < 90) {
      label = 'Good';
      color = 'text-green-600 bg-green-100';
    } else {
      label = 'Strong';
      color = 'text-green-700 bg-green-200';
    }

    return { score, label, color, checks };
  };

  const passwordStrength = calculatePasswordStrength(newPassword);

  // Validate form (reused logic from PasswordChangeCard.js lines 84-115)
  const validateForm = () => {
    const newErrors = {
      newPassword: '',
      confirmPassword: ''
    };

    if (!newPassword) {
      newErrors.newPassword = 'New password is required';
    } else if (newPassword.length < 8) {
      newErrors.newPassword = 'Password must be at least 8 characters';
    } else if (!/[A-Z]/.test(newPassword)) {
      newErrors.newPassword = 'Password must contain at least one uppercase letter';
    } else if (!/[a-z]/.test(newPassword)) {
      newErrors.newPassword = 'Password must contain at least one lowercase letter';
    } else if (!/[0-9]/.test(newPassword)) {
      newErrors.newPassword = 'Password must contain at least one number';
    }

    if (!confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your new password';
    } else if (newPassword !== confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    setErrors(newErrors);
    return !newErrors.newPassword && !newErrors.confirmPassword;
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      // DEPRECATED: Password reset via Supabase removed in Phase 6 Azure migration
      // TODO: Implement Azure auth password reset API
      showAlert(
        'Feature Not Available',
        'Password reset is temporarily unavailable during system migration. Please contact info@fleetskipper.com for assistance.',
        'info'
      );
      setIsSubmitting(false);
      return;

      /* LEGACY SUPABASE CODE - COMMENTED OUT FOR PHASE 6
      // Update password (user already authenticated via recovery token)
      // Uses supabaseAuth.js updatePassword() function (line 464-488)
      const { error: updateError } = await updatePassword(newPassword);

      if (updateError) {
        showAlert('Password Reset Failed', updateError.message || 'Failed to update password', 'error');
        setIsSubmitting(false);
        return;
      }

      // Success - clear URL hash and redirect
      showAlert('Password Reset Successful', 'Your password has been changed. Please sign in with your new password.', 'success');

      // Clear the recovery token from URL (security measure)
      window.history.replaceState(null, '', window.location.pathname);

      // Notify parent to show login screen
      onResetComplete?.();
      */
    } catch (error) {
      console.error('Password reset error:', error);
      showAlert('Password Reset Failed', error.message || 'An unexpected error occurred', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Check if form is valid for submit button
  const isFormValid = newPassword.length >= 8 &&
                     /[A-Z]/.test(newPassword) &&
                     /[a-z]/.test(newPassword) &&
                     /[0-9]/.test(newPassword) &&
                     newPassword === confirmPassword;

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-cyan-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-md w-full border border-gray-100">
        {/* Header - matches LoginScreen.jsx design */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-cyan-600 rounded-xl mb-4">
            <Ship className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-2xl font-bold text-slate-800 mb-2">Set New Password</h1>
          <p className="text-gray-600">Choose a strong password for your account</p>
        </div>

        {/* Password Reset Form */}
        <form onSubmit={handleSubmit} className="space-y-5">
          {/* New Password Field */}
          <div>
            <label htmlFor="newPassword" className="block text-sm font-medium text-gray-700 mb-1">
              New Password
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Lock className="h-5 w-5 text-gray-400" />
              </div>
              <input
                id="newPassword"
                type={showNewPassword ? 'text' : 'password'}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className={`w-full pl-10 pr-12 py-3 border ${
                  errors.newPassword ? 'border-red-300' : 'border-gray-300'
                } rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 transition-colors`}
                placeholder="Enter new password"
                disabled={isSubmitting}
                autoFocus
              />
              <button
                type="button"
                onClick={() => setShowNewPassword(!showNewPassword)}
                className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                tabIndex={-1}
              >
                {showNewPassword ? (
                  <EyeOff className="h-5 w-5" />
                ) : (
                  <Eye className="h-5 w-5" />
                )}
              </button>
            </div>
            {errors.newPassword && (
              <p className="mt-1 text-sm text-red-600">{errors.newPassword}</p>
            )}

            {/* Password Strength Indicator - reused from PasswordChangeCard.js */}
            {newPassword && (
              <div className="mt-2">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs text-gray-600">Password strength:</span>
                  <span className={`text-xs font-medium px-2 py-0.5 rounded ${passwordStrength.color}`}>
                    {passwordStrength.label}
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-1.5">
                  <div
                    className={`h-1.5 rounded-full transition-all ${
                      passwordStrength.score < 40
                        ? 'bg-red-600'
                        : passwordStrength.score < 70
                        ? 'bg-yellow-600'
                        : 'bg-green-600'
                    }`}
                    style={{ width: `${passwordStrength.score}%` }}
                  ></div>
                </div>

                {/* Password Requirements Checklist */}
                <div className="mt-2 space-y-1">
                  <div className="flex items-center text-xs">
                    {passwordStrength.checks?.length ? (
                      <CheckCircle className="h-3 w-3 text-green-600 mr-1" />
                    ) : (
                      <XCircle className="h-3 w-3 text-gray-400 mr-1" />
                    )}
                    <span className={passwordStrength.checks?.length ? 'text-green-600' : 'text-gray-500'}>
                      At least 8 characters
                    </span>
                  </div>
                  <div className="flex items-center text-xs">
                    {passwordStrength.checks?.uppercase ? (
                      <CheckCircle className="h-3 w-3 text-green-600 mr-1" />
                    ) : (
                      <XCircle className="h-3 w-3 text-gray-400 mr-1" />
                    )}
                    <span className={passwordStrength.checks?.uppercase ? 'text-green-600' : 'text-gray-500'}>
                      One uppercase letter
                    </span>
                  </div>
                  <div className="flex items-center text-xs">
                    {passwordStrength.checks?.lowercase ? (
                      <CheckCircle className="h-3 w-3 text-green-600 mr-1" />
                    ) : (
                      <XCircle className="h-3 w-3 text-gray-400 mr-1" />
                    )}
                    <span className={passwordStrength.checks?.lowercase ? 'text-green-600' : 'text-gray-500'}>
                      One lowercase letter
                    </span>
                  </div>
                  <div className="flex items-center text-xs">
                    {passwordStrength.checks?.number ? (
                      <CheckCircle className="h-3 w-3 text-green-600 mr-1" />
                    ) : (
                      <XCircle className="h-3 w-3 text-gray-400 mr-1" />
                    )}
                    <span className={passwordStrength.checks?.number ? 'text-green-600' : 'text-gray-500'}>
                      One number
                    </span>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Confirm Password Field */}
          <div>
            <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-1">
              Confirm New Password
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Lock className="h-5 w-5 text-gray-400" />
              </div>
              <input
                id="confirmPassword"
                type={showConfirmPassword ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className={`w-full pl-10 pr-12 py-3 border ${
                  errors.confirmPassword ? 'border-red-300' : 'border-gray-300'
                } rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 transition-colors`}
                placeholder="Confirm new password"
                disabled={isSubmitting}
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                tabIndex={-1}
              >
                {showConfirmPassword ? (
                  <EyeOff className="h-5 w-5" />
                ) : (
                  <Eye className="h-5 w-5" />
                )}
              </button>
            </div>
            {errors.confirmPassword && (
              <p className="mt-1 text-sm text-red-600">{errors.confirmPassword}</p>
            )}
          </div>

          {/* Submit Button - matches LoginScreen.jsx style */}
          <button
            type="submit"
            disabled={isSubmitting || !isFormValid}
            className="w-full bg-cyan-600 text-white py-3 px-4 rounded-lg hover:bg-cyan-700 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {isSubmitting ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                Updating Password...
              </>
            ) : (
              'Reset Password'
            )}
          </button>
        </form>

        {/* Footer - matches LoginScreen.jsx design */}
        <div className="mt-8 pt-6 border-t border-gray-100">
          <p className="text-xs text-center text-gray-500">
            After resetting your password, you'll be redirected to sign in
          </p>
        </div>
      </div>
    </div>
  );
};

export default ResetPasswordScreen;
